import type { IFile, IFlatNode } from '../../types/file';
import { convertFlatToTree, type IFolderNode } from '../FolderPreview/utils';

export interface TreeSelectNode {
  value: string;
  title: string;
  disabled?: boolean;
  isLeaf?: boolean;
  children?: TreeSelectNode[];
}

export interface IParsedDocument {
  treeSelectData: TreeSelectNode[];
  nodeMap: Map<string, IFolderNode>;
}

export interface IPaneState {
  id: string;
  selectedFileId: string | null;
}

const buildTreeSelectData = (
  node: IFolderNode,
  nodeMap: Map<string, IFolderNode>
): TreeSelectNode => {
  const isFolder = Array.isArray(node.children);

  if (!isFolder) {
    nodeMap.set(node.id, node);
  }

  return {
    value: node.id,
    title: node.name,
    disabled: isFolder,
    isLeaf: !isFolder,
    children: isFolder
      ? node.children!.map(child => buildTreeSelectData(child, nodeMap))
      : undefined,
  };
};

export const extractFilesFromJson = (json: unknown): IFile[] => {
  const results: IFile[] = [];
  const seen = new Set<string>();

  const isFileNode = (obj: Record<string, unknown>): boolean =>
    typeof obj.id === 'string' &&
    typeof obj.name === 'string' &&
    typeof obj.path === 'string';

  const walk = (node: unknown): void => {
    if (!node || typeof node !== 'object') return;

    if (Array.isArray(node)) {
      node.forEach(walk);
      return;
    }

    const record = node as Record<string, unknown>;

    if (isFileNode(record)) {
      if (!seen.has(record.id as string)) {
        seen.add(record.id as string);
        results.push({
          id: record.id as string,
          name: record.name as string,
          path: record.path as string,
          disk: (record.disk as string) ?? '',
          bucket: (record.bucket as string | null) ?? null,
          size: (record.size as number) ?? 0,
          extension: (record.extension as string) ?? '',
          createdAt: (record.createdAt as string) ?? '',
          updatedAt: (record.updatedAt as string) ?? '',
          deletedAt: (record.deletedAt as string | null) ?? null,
          createdBy: (record.createdBy as IFile['createdBy']) ?? null,
          folder: record.folder as IFlatNode[] | null | undefined,
        });
      }
      return;
    }

    Object.values(record).forEach(walk);
  };

  walk(json);
  return results;
};

export const parseDocumentData = (data: IFile[]): IParsedDocument => {
  const nodeMap = new Map<string, IFolderNode>();
  const treeSelectData: TreeSelectNode[] = [];

  for (const file of data) {
    const isZip =
      file.extension?.toLowerCase() === 'zip' &&
      Array.isArray(file.folder) &&
      file.folder.length > 0;

    if (isZip) {
      const treeRoot = convertFlatToTree(file.folder!);
      treeSelectData.push(buildTreeSelectData(treeRoot, nodeMap));
    } else {
      const node: IFolderNode = {
        id: file.id,
        name: file.name,
        path: file.path,
        size: file.size,
        disk: file.disk,
        bucket: file.bucket ?? undefined,
        extension: file.extension,
      };
      nodeMap.set(file.id, node);
      treeSelectData.push({ value: file.id, title: file.name, isLeaf: true });
    }
  }

  return { treeSelectData, nodeMap };
};
